import { useCallback, useEffect, useRef, useState } from "react";
import { fetchCars, createCar, updateCar, deleteCar }                                   from "../../api/cars";
import { fetchSpares, createSpare, updateSpare, deleteSpare }                           from "../../api/spares";
import { fetchAccessories, createAccessory, updateAccessory, deleteAccessory }          from "../../api/accessories";
import { fetchMechanics, createMechanic, updateMechanic, deleteMechanic }               from "../../api/mechanics";
import { fetchCustomers, createCustomer, updateCustomer, deleteCustomer }               from "../../api/customers";
import { fetchBookings, createBooking, updateBooking, deleteBooking, updateBookingStatus, convertBookingToWorkOrder } from "../../api/bookings";
import { fetchWorkOrders, createWorkOrder, updateWorkOrder, deleteWorkOrder, updateWorkOrderStatus, createInvoiceFromWorkOrder } from "../../api/workOrders";
import { fetchQuotations, createQuotation, updateQuotation, deleteQuotation, updateQuotationStatus, convertQuotationToInvoice } from "../../api/quotations";
import { fetchInvoices, createInvoice, updateInvoice, deleteInvoice, recordPayment }   from "../../api/invoices";
import { fetchServices, createService, updateService, deleteService }                  from "../../api/services";
import { fetchDashboard }                                                               from "../../api/dashboard";
import { fetchReport }                                                                  from "../../api/reports";
import { logoutGarage, getGarageInfo }                                                  from "../../api/garageAuth";
import { useNavigate }                                                                  from "react-router-dom";
import { useInventory }                                                                 from "../../hooks/useInventory";
import { useResource }                                                                  from "../../hooks/useResource";
import GarageProfile                                                                  from "../GarageProfile";
import "../../styles/garage/garage-management.css";

// ── Inventory API maps ─────────────────────────────────────────
const fetchVehicles     = () => fetchCars({ type: "vehicle" }, 1, 100);
const fetchHireVehicles = () => fetchCars({ type: "hire" }, 1, 100);

const FETCH_FN = {
  cars: fetchVehicles, hire: fetchHireVehicles,
  spares: fetchSpares, accessories: fetchAccessories, mechanics: fetchMechanics,
};
const DELETE_FN = {
  cars: deleteCar, hire: deleteCar,
  spares: deleteSpare, accessories: deleteAccessory, mechanics: deleteMechanic,
};
// keyed by form type (INVENTORY_CONFIG[tab].type)
const CREATE_FN = { vehicle: createCar, spare: createSpare, accessory: createAccessory, mechanic: createMechanic };
const UPDATE_FN = { vehicle: updateCar, spare: updateSpare, accessory: updateAccessory, mechanic: updateMechanic };

const REQUIRED = {
  vehicle:   ["make", "model", "year", "condition", "location", "price"],
  spare:     ["category", "name", "price", "quantity"],
  accessory: ["category", "brand", "title", "price", "stock"],
  mechanic:  ["name", "phone", "specialty"],
};

// ── Theme ──────────────────────────────────────────────────────
const C = {
  red: "#c1392a", redL: "#f9ecea",
  navy: "#1a1f2e", navyM: "#252b3b", navyL: "#2e3547",
  bg: "#f5f6f8", surface: "#fff", border: "#e2e8f0",
  text: "#1a1f2e", muted: "#64748b", slate: "#94a3b8",
  green: "#16a34a", greenL: "#dcfce7",
  amber: "#d97706", amberL: "#fef3c7",
};



// ── Lookup tables ──────────────────────────────────────────────
const STATUS_STYLE = {
  Available:       { bg: C.greenL, color: C.green  },
  Active:          { bg: C.greenL, color: C.green  },
  "In progress":   { bg: "#dbeafe", color: "#2563eb" },
  New:             { bg: "#dbeafe", color: "#2563eb" },
  Reserved:        { bg: C.amberL, color: C.amber  },
  "Waiting parts": { bg: C.amberL, color: C.amber  },
  "Off duty":      { bg: "#f1f5f9", color: C.muted },
  Sold:            { bg: "#f1f5f9", color: C.muted },
  Delayed:         { bg: C.redL,   color: C.red    },
  "On leave":      { bg: "#f1f5f9", color: C.muted },
  Pending:         { bg: C.amberL, color: C.amber  },
  Confirmed:       { bg: "#dbeafe", color: "#2563eb" },
  Completed:       { bg: C.greenL, color: C.green  },
  Cancelled:       { bg: "#f1f5f9", color: C.muted },
  Draft:           { bg: "#f1f5f9", color: C.muted },
  Sent:            { bg: "#dbeafe", color: "#2563eb" },
  Accepted:        { bg: C.greenL, color: C.green  },
  Rejected:        { bg: C.redL,   color: C.red    },
  Unpaid:          { bg: C.amberL, color: C.amber  },
  Partial:         { bg: "#dbeafe", color: "#2563eb" },
  Paid:            { bg: C.greenL, color: C.green  },
  Overdue:         { bg: C.redL,   color: C.red    },
};

export const GARAGE_SERVICE_OPTIONS = [
  "General service", "Full service", "Engine oil change", "Oil filter replacement",
  "Brake inspection", "Brake pad replacement", "Wheel alignment", "Wheel balancing",
  "Tyre replacement", "Battery replacement", "Air conditioning service",
  "Transmission service", "Suspension repair", "Electrical diagnostics",
  "Engine diagnostics", "Body repair", "Car wash", "Other",
];

const VIEW_TITLES = {
  dashboard: "Dashboard", bookings: "Bookings", workorders: "Work Orders",
  quotations: "Quotations", customers: "Customers",
  invoices: "Invoices", reports: "Reports",
  services: "Services",
  mechanics: "Mechanics",
};

const INVENTORY_CONFIG = {
  mechanics:   { label: "Mechanics",    addLabel: "Add mechanic",    type: "mechanic"  },
};

const FORM_TITLES = {
  vehicle: "Add car", spare: "Add spare part",
  accessory: "Add accessory", mechanic: "Add mechanic",
};

const INVENTORY_VIEWS = new Set(["mechanics"]);



const fmtPrice = (price, currency = "KES") =>
  `${currency === "USD" ? "$" : "KSh "} ${Number(price || 0).toLocaleString("en-US")}`;

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

// ── useInventory hook ──────────────────────────────────────────
const extractList = (result) => {
  if (Array.isArray(result))       return result;
  if (Array.isArray(result?.cars)) return result.cars;
  if (Array.isArray(result?.data)) return result.data;
  return [];
};

// ── UI primitives ──────────────────────────────────────────────
function Badge({ status }) {
  const s = STATUS_STYLE[status] || { bg: "#f1f5f9", color: C.muted };
  return (
    <span style={{ background: s.bg, color: s.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {status}
    </span>
  );
}

function PrimaryBtn({ onClick, children, disabled }) {
  return (
    <button className="primary-btn"
      onClick={onClick}
      disabled={disabled}
      style={{ background: disabled ? C.muted : C.red, color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 6, opacity: disabled ? .7 : 1 }}
    >
      {children}
    </button>
  );
}

function OutlineBtn({ onClick, children }) {
  return (
    <button className="outline-btn" onClick={onClick} style={{ background: "transparent", color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
      {children}
    </button>
  );
}

function DeleteBtn({ onClick }) {
  return (
    <button className="delete-btn" onClick={onClick} style={{ background: "#fee2e2", color: "#dc2626", border: "none", borderRadius: 7, padding: "6px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
      Delete
    </button>
  );
}

function DrawerShell({ title, submitLabel = "Save", onSubmit, onClose, children }) {
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit();
      setSaved(true);
      setTimeout(() => onClose(true), 700);
    } catch (err) {
      setError(err.message || "Save failed. Check your connection.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="drawer-backdrop" onClick={() => onClose(false)} />
      <div className="drawer-panel">
        <div className="drawer-header">
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h2>
            <p style={{ fontSize: 12, color: C.muted, margin: "3px 0 0" }}>Fill in the details below</p>
          </div>
          <button onClick={() => onClose(false)} style={{ width: 32, height: 32, border: `1px solid ${C.border}`, borderRadius: 7, background: "transparent", cursor: "pointer", fontSize: 20, color: C.muted, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>×</button>
        </div>
        <div className="drawer-body">{children}</div>
        <div className="drawer-footer">
          {error && <p style={{ color: C.red, fontSize: 12, marginBottom: 10 }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <OutlineBtn onClick={() => onClose(false)}>Cancel</OutlineBtn>
            <PrimaryBtn onClick={submit} disabled={submitting || saved}>
              {saved ? "✓ Saved!" : submitting ? "Saving..." : submitLabel}
            </PrimaryBtn>
          </div>
        </div>
      </div>
    </>
  );
}

function AvatarCircle({ name, size = 28 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: C.navyM, color: "#fff", fontSize: size * 0.38, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {getInitials(name)}
    </div>
  );
}

// ── Form primitives ────────────────────────────────────────────
const iStyle = { border: `1px solid ${C.border}`, borderRadius: 7, padding: "9px 12px", fontSize: 13, color: C.text, outline: "none", width: "100%", boxSizing: "border-box", fontFamily: "inherit", background: "#fff" };

function Field({ label, required, children, hint }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.text }}>
        {label}{required && <span style={{ color: C.red }}> *</span>}
      </label>
      {children}
      {hint && <p style={{ fontSize: 11, color: C.muted, margin: 0 }}>{hint}</p>}
    </div>
  );
}

const FInput    = (props)            => <input    style={iStyle} {...props} />;
const FTextarea = ({ rows=3, ...p }) => <textarea style={{ ...iStyle, resize: "vertical", fontFamily: "inherit" }} rows={rows} {...p} />;

function FSelect({ children, ...props }) {
  return (
    <select style={{ ...iStyle, appearance: "none", paddingRight: 28, backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath d='M2 3.5L5 6.5L8 3.5' stroke='%2364748b' stroke-width='1.4' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")", backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }} {...props}>
      {children}
    </select>
  );
}

const Divider  = ({ title }) => (
  <div style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 6, marginTop: 8 }}>
    <span style={{ fontSize: 11, fontWeight: 700, color: C.text, textTransform: "uppercase", letterSpacing: ".06em" }}>{title}</span>
  </div>
);

const Grid2 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>{children}</div>;
const Grid3 = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>{children}</div>;

// ── Sidebar ────────────────────────────────────────────────────
const MAIN_NAV = [
  { id:"dashboard", label:"Dashboard", icon:"fa-gauge-high" },
  { id:"bookings", label:"Bookings", icon:"fa-calendar-check" },
  { id:"services", label:"Services", icon:"fa-screwdriver" },
  { id:"workorders", label:"Work Orders", icon:"fa-screwdriver-wrench" },
  { id:"quotations", label:"Quotations", icon:"fa-file-invoice-dollar" },
  { id:"customers", label:"Customers", icon:"fa-users" },
];
const FINANCE_NAV = [
  { id:"invoices", label:"Invoices", icon:"fa-receipt" },
  { id:"reports", label:"Reports", icon:"fa-chart-line" },
];
const INV_NAV = [{ id:"mechanics", label:"Mechanics", icon:"fa-user-gear" }];
const PROFILE_NAV = [{ id:"profile", label:"Garage profile", icon:"fa-building" }];

function NavLabel({ text }) {
  return <div className="garage-nav-label">{text}</div>;
}

function NavItem({ label, icon, active, onClick }) {
  return (
    <button type="button" className={`garage-nav-item${active ? " is-active" : ""}`} onClick={onClick}>
      <i className={`fa-solid ${icon}`} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function Sidebar({ view, onView, onLogout }) {
  const openAdvancedTools = () => window.dispatchEvent(new CustomEvent("garage-advanced-tools"));
  return (
    <aside className="garage-sidebar">
      <div className="garage-brand">
        <div className="garage-brand-mark"><i className="fa-solid fa-car-side" aria-hidden="true" /></div>
        <div>
          <div className="garage-brand-name">My Car Kenya</div>
          <div className="garage-brand-sub">Garage Management</div>
        </div>
      </div>

      <nav className="garage-navigation" aria-label="Garage navigation">
        <NavLabel text="Main" />
        {MAIN_NAV.map(i => <NavItem key={i.id} {...i} active={view === i.id} onClick={() => onView(i.id)} />)}
        <NavLabel text="Finance" />
        {FINANCE_NAV.map(i => <NavItem key={i.id} {...i} active={view === i.id} onClick={() => onView(i.id)} />)}
        <NavLabel text="Inventory" />
        {INV_NAV.map(i => <NavItem key={i.id} {...i} active={view === i.id} onClick={() => onView(i.id)} />)}
        <NavLabel text="Account" />
        {PROFILE_NAV.map(i => <NavItem key={i.id} {...i} active={view === i.id} onClick={() => onView(i.id)} />)}
      </nav>

      <div className="garage-user">
        <div className="garage-user-avatar">AD</div>
        <div className="garage-user-copy">
          <div className="garage-user-name">Admin</div>
          <div className="garage-user-role">Garage Manager</div>
          <button type="button" className="garage-signout" onClick={onLogout}>Sign out</button>
        </div>
        <button type="button" className="garage-user-more" onClick={openAdvancedTools}
          title="Advanced tools: edit or delete the selected record after password verification"
          aria-label="Open advanced tools"><i className="fa-solid fa-ellipsis-vertical" aria-hidden="true" /></button>
      </div>
    </aside>
  );
}

// ── Dashboard ──────────────────────────────────────────────────
function StatCard({ label, value, delta, accent }) {
  return (
    <div className={`stat-card${accent ? " stat-card-accent" : ""}`}>

      <div style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: accent ? C.red : C.text, lineHeight: 1 }}>{value}</div>
      {delta && <div style={{ fontSize: 10, color: C.green, marginTop: 4, fontWeight: 500 }}>{delta}</div>}
    </div>
  );
}

function Card({ title, sub, children }) {
  return (
    <div className="surface-card">
      <div className="surface-card-title">
        <span>{title}</span>{sub && <span className="surface-card-sub">{sub}</span>}
      </div>
      {children}
    </div>
  );
}

const TH = ({ c }) => <th style={{ textAlign: "left", padding: "0 12px 10px", fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em", whiteSpace: "nowrap" }}>{c}</th>;
const TD = ({ children, bold }) => <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: bold ? 600 : 400, verticalAlign: "middle" }}>{children}</td>;

const fmtCompact = (n = 0) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `KSh ${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `KSh ${Math.round(v / 1_000)}k`;
  return `KSh ${v}`;
};

const Msg = ({ children, color = C.muted }) => (
  <p style={{ textAlign: "center", color, padding: "40px 0", fontSize: 13 }}>{children}</p>
);

function Dashboard() {
  const { data, loading, error, reload } = useResource((signal) => fetchDashboard(signal));

  if (loading && !data) return <Msg>Loading dashboard...</Msg>;
  if (error && !data)
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <Msg color={C.red}>Error: {error}</Msg>
        <OutlineBtn onClick={reload}>Retry</OutlineBtn>
      </div>
    );

  const { summary: s, activeJobs, mechanicActivity, popularServices } = data;
  const maxCount = Math.max(1, ...popularServices.map((p) => Number(p.count) || 0));
  const change = s.revenueChangePct;

  return (
    <div className="page-section dashboard-page">
      <div className="dashboard-stats">
        <StatCard label="Pending jobs"       value={s.pendingJobs ?? 0} />
        <StatCard label="Jobs today"         value={s.jobsToday ?? 0} delta={s.jobsCompletedToday != null ? `${s.jobsCompletedToday} completed` : null} />
        <StatCard label="Pending bookings"   value={s.pendingBookings ?? 0} />
        <StatCard label="Outstanding amount" value={fmtCompact(s.outstandingAmount)} accent />
        <StatCard label="Mechanics on duty"  value={s.mechanicsOnDuty ?? 0} delta={s.mechanicsActive != null ? `${s.mechanicsActive} currently active` : null} />
        <StatCard label="Revenue this month" value={fmtCompact(s.revenueThisMonth)} accent delta={change != null ? `${change >= 0 ? "↑" : "↓"} ${Math.abs(change)}% vs last month` : null} />
      </div>

      <Card title="Active jobs" sub="today">
        {!activeJobs.length ? <Msg>No active jobs right now.</Msg> : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Plate","Vehicle","Service","Mechanic","Status","Est. Cost"].map(h => <TH key={h} c={h} />)}</tr></thead>
              <tbody>
                {activeJobs.map((row, i) => (
                  <tr key={row.id ?? i} style={{ borderTop: `1px solid ${C.border}` }}>
                    <TD><span style={{ background: C.navy, color: "#fff", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4 }}>{row.plate}</span></TD>
                    <TD>{row.vehicle}</TD>
                    <TD>{row.service}</TD>
                    <TD>{row.mechanicName ?? row.mechanic?.name ?? "Unassigned"}</TD>
                    <TD><Badge status={row.status} /></TD>
                    <TD bold>{fmtPrice(row.estimatedCost)}</TD>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="dashboard-two-col">
        <Card title="Mechanic activity" sub="this week">
          {!mechanicActivity.length ? <Msg>No activity yet.</Msg> : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {[["Mechanic","left"],["Assigned","center"],["Done","center"]].map(([h, a]) => (
                    <th key={h} style={{ textAlign: a, padding: "0 0 8px", fontSize: 10, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".06em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mechanicActivity.map(m => (
                  <tr key={m.name} style={{ borderTop: `1px solid ${C.border}` }}>
                    <td style={{ padding: "9px 0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <AvatarCircle name={m.name} size={26} />
                        <span style={{ fontSize: 12 }}>{m.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: "9px 0", textAlign: "center", fontSize: 12, fontWeight: 600 }}>{m.assigned}</td>
                    <td style={{ padding: "9px 0", textAlign: "center", fontSize: 12, fontWeight: 700, color: C.green }}>{m.done}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Popular services" sub="this month">
          {!popularServices.length ? <Msg>No completed jobs yet.</Msg> : popularServices.map(p => (
            <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 12, flex: "0 0 130px" }}>{p.name}</span>
              <div style={{ flex: 1, height: 5, background: C.border, borderRadius: 3 }}>
                <div style={{ width: `${((Number(p.count) || 0) / maxCount) * 100}%`, height: "100%", background: C.red, opacity: .75, borderRadius: 3 }} />
              </div>
              <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, flexShrink: 0 }}>{p.count} jobs</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ── Inventory tables ───────────────────────────────────────────


export {
  fetchCars, createCar, updateCar, deleteCar,
  fetchSpares, createSpare, updateSpare, deleteSpare,
  fetchAccessories, createAccessory, updateAccessory, deleteAccessory,
  fetchMechanics, createMechanic, updateMechanic, deleteMechanic,
  fetchCustomers, createCustomer, updateCustomer, deleteCustomer,
  fetchBookings, createBooking, updateBooking, deleteBooking, updateBookingStatus, convertBookingToWorkOrder,
  fetchWorkOrders, createWorkOrder, updateWorkOrder, deleteWorkOrder, updateWorkOrderStatus, createInvoiceFromWorkOrder,
  fetchQuotations, createQuotation, updateQuotation, deleteQuotation, updateQuotationStatus, convertQuotationToInvoice,
  fetchInvoices, createInvoice, updateInvoice, deleteInvoice, recordPayment,
  fetchServices, createService, updateService, deleteService,
  fetchDashboard, fetchReport, logoutGarage, getGarageInfo,
  useInventory, useResource,
  fetchVehicles, fetchHireVehicles, FETCH_FN, DELETE_FN, CREATE_FN, UPDATE_FN, REQUIRED,
  C, STATUS_STYLE, VIEW_TITLES, INVENTORY_CONFIG, FORM_TITLES, INVENTORY_VIEWS,
  fmtPrice, getInitials, extractList,
  Badge, PrimaryBtn, OutlineBtn, DeleteBtn, DrawerShell, AvatarCircle, Field, FInput, FTextarea, FSelect,
  Divider, Grid2, Grid3, Sidebar, StatCard, Card, TH, TD, fmtCompact, Msg, Dashboard,
};
